import * as React from "react"
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { UniqueIdentifier } from "@dnd-kit/core";
import { SortableRows } from "./sortable-rows/sortable-rows";
import { ISortableBaseItem } from "../sortable-list/sortable-list";
import { Icon } from "../icons/icon";
import styles from "./editable-table.module.scss";
import { useSharedTranslations } from "../../lib/i18n";
import cn from "classnames";

interface IEditableTableProps<T extends ISortableBaseItem> {
    data: T[]
    columns: ColumnDef<T, any>[];
    onChange: (data: T[]) => void;
    onDeleteRow: (id: UniqueIdentifier) => void;
    readonly?: boolean;
}

export function EditableTable<T extends ISortableBaseItem>({
                                                               data,
                                                               columns,
                                                               onChange,
                                                               onDeleteRow,
                                                               readonly = false
                                                           }: IEditableTableProps<T>) {
    const t = useSharedTranslations();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const findRowById = (id: UniqueIdentifier) => {
        return table.getRowModel().rows.find((r) => r.original.id === id);
    }

    return (
        <div className={styles.container}>
            <div className={styles.table}>
                <div className={styles.thead}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <div key={headerGroup.id} className={styles.headerRow}>
                            {headerGroup.headers.map(header => (
                                <div key={header.id}
                                     className={cn(styles.headerCell, headerGroup.headers.length === 1 ? "singleColumn" : "")}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div className={styles.tbody}>
                    <SortableRows<T>
                        items={data}
                        onChange={onChange}
                        renderItem={(item: T) => (
                            <SortableRows.Item id={item.id}>
                                <div className={styles.fullRow}>
                                    {!readonly && <SortableRows.DragHandle/>}
                                    <div key={item.id} className={cn(styles.bodyRow, readonly ? styles.readonlyRow : "")}>
                                        {findRowById(item.id)?.getVisibleCells().map(cell => (
                                            <div key={cell.id}
                                                 className={cn(styles.cell, findRowById(item.id)?.getVisibleCells().length === 1 ? "singleColumn" : "")}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </div>
                                        ))}
                                    </div>
                                    {!readonly && <div onClick={() => onDeleteRow(item.id)} className={styles.deleteBtn}>
                                        <Icon glyph="trash" title={t("delete")}/>
                                    </div>}
                                </div>
                            </SortableRows.Item>
                        )}
                    />
                </div>
            </div>
        </div>
    )
}
